import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { CheckCircle, XCircle, Copy } from "lucide-react";
import { ConorbitLoader } from "@/components/ui/ConorbitLoader";
import { Button } from "@/components/ui/button";

/** Extract real error text from a supabase FunctionsHttpError response body */
async function extractFnError(err: any): Promise<string> {
  // err.context is the raw Response object when the function returned non-2xx
  try {
    const body = await err?.context?.json?.();
    if (body?.error) return body.error;
    const text = await err?.context?.text?.();
    if (text) return text;
  } catch {}
  return err?.message ?? 'Unknown error';
}

const OAuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Processing authorization…');
  const [platform, setPlatform] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        // Meta/Threads pass errors as query params
        const errorParam     = searchParams.get('error');
        const errorDesc      = searchParams.get('error_description');
        const errorMessage   = searchParams.get('error_message'); // Threads
        const errorReason    = searchParams.get('error_reason');

        if (errorParam || errorMessage) {
          const detail = errorDesc || errorMessage || errorReason || errorParam || 'Authorization denied';
          throw new Error(detail);
        }

        if (!code) throw new Error('No authorization code received from provider');

        const storedState    = localStorage.getItem('oauth_state');
        const storedPlatform = localStorage.getItem('oauth_platform') ?? '';
        const codeVerifier   = localStorage.getItem('oauth_code_verifier');
        const userId         = localStorage.getItem('oauth_user_id');
        const storedTs       = Number(localStorage.getItem('oauth_ts') ?? '0');

        setPlatform(storedPlatform);

        if (storedTs && Date.now() - storedTs > 10 * 60 * 1000) {
          throw new Error('OAuth session expired. Please close this window and try connecting again.');
        }
        if (state !== storedState) throw new Error('State mismatch — possible CSRF attempt. Please try again.');
        if (!storedPlatform || !userId) throw new Error('Missing OAuth session data. Please close this window and try connecting again.');

        const redirectUri = localStorage.getItem('oauth_redirect_uri')
          ?? `${window.location.origin}/oauth/callback`;
        const orgId = localStorage.getItem('oauth_org_id');

        setMessage(`Completing ${storedPlatform} authorization…`);

        // Get current session so the edge function can verify the caller
        const { data: { session } } = await supabase.auth.getSession();

        const { data, error: callbackError } = await supabase.functions.invoke(
          `${storedPlatform}-oauth-callback`,
          {
            body: { code, state, codeVerifier, userId, redirectUri, ...(orgId ? { orgId } : {}) },
            headers: session ? { Authorization: `Bearer ${session.access_token}` } : {},
          }
        );

        if (callbackError) {
          const msg = await extractFnError(callbackError);
          throw new Error(msg);
        }
        if (data?.error) throw new Error(data.error);

        // Clean up
        ['oauth_state','oauth_platform','oauth_code_verifier','oauth_user_id','oauth_redirect_uri','oauth_org_id','oauth_ts']
          .forEach(k => localStorage.removeItem(k));

        setStatus('success');
        setMessage(`Your ${data.account?.platform ?? storedPlatform} account is now connected.`);

        if (window.opener) {
          window.opener.postMessage({ type: 'oauth-success', data }, window.location.origin);
          setTimeout(() => window.close(), 1800);
        } else {
          setTimeout(() => navigate('/dashboard'), 2000);
        }

      } catch (err: any) {
        console.error('[OAuthCallback]', err);
        setStatus('error');
        setMessage(err.message ?? 'Failed to complete OAuth flow');

        if (window.opener) {
          window.opener.postMessage({ type: 'oauth-error', error: err.message }, window.location.origin);
          setTimeout(() => window.close(), 3000);
        } else {
          setTimeout(() => navigate('/dashboard'), 4000);
        }
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  const callbackUrl = `${window.location.origin}/oauth/callback`;

  const copyUrl = () => {
    navigator.clipboard.writeText(callbackUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const isRedirectError =
    message.toLowerCase().includes('url blocked') ||
    message.toLowerCase().includes('redirect') ||
    message.toLowerCase().includes('not whitelisted') ||
    message.toLowerCase().includes('redirect_uri');

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/5 p-4">
      <Card className="p-8 max-w-md w-full text-center space-y-4">
        {status === 'loading' && (
          <>
            <ConorbitLoader variant="inline" label="Connecting Account…" />
            <p className="text-sm text-muted-foreground">{message}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto" />
            <h2 className="text-xl font-semibold">Connected!</h2>
            <p className="text-muted-foreground text-sm">{message}</p>
            <p className="text-xs text-muted-foreground">This window will close automatically…</p>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle className="h-12 w-12 text-destructive mx-auto" />
            <h2 className="text-xl font-semibold">Connection Failed</h2>
            <p className="text-sm text-muted-foreground break-words">{message}</p>

            {isRedirectError && (
              <div className="text-left rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-2">
                <p className="text-xs font-semibold text-amber-800">
                  Fix: Add this URL as an authorised redirect URI in your {platform || 'provider'} app settings:
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-[11px] bg-white border border-amber-200 rounded px-2 py-1 break-all text-amber-900">
                    {callbackUrl}
                  </code>
                  <Button size="sm" variant="outline" className="shrink-0 h-7 text-xs" onClick={copyUrl}>
                    <Copy className="w-3 h-3 mr-1" />
                    {copied ? 'Copied!' : 'Copy'}
                  </Button>
                </div>
                <p className="text-[11px] text-amber-700">
                  After adding it, save changes and try connecting again.
                </p>
              </div>
            )}

            <p className="text-xs text-muted-foreground">
              {window.opener ? 'You can close this window and try again.' : 'Redirecting back…'}
            </p>
          </>
        )}
      </Card>
    </div>
  );
};

export default OAuthCallback;
